import Button from '@atoms/Button/Button';
import DropDown from '@atoms/DropDown/DropDown';
import Icon from '@atoms/Icon/Icon';
import Tooltip from '@atoms/Tooltip/Tooltip';
import { DEBOUNCE_IN_MS, MINIMUM_QUERY_LENGTH } from '@constants/constants';
import useStore from '@hooks/useStore';
import useTranslations from '@hooks/useTranslations';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { Configure, SearchBox, useInstantSearch } from 'react-instantsearch';
import { deleteCookie, getCookie, setCookie } from 'utils/cookies';

import styles from './SearchForm.module.scss';

const SEARCH_FORM_ID = 'search-form';
const TARGET_TAG_URL_PARAM = 'doelgroep';
const TARGET_TAG_FILTER_NAME = 'target_tags';
const TARGET_FILTER_INFO_ID = 'target-filter-info';

let timerId: NodeJS.Timeout | null = null;

// DOCS: https://www.algolia.com/doc/guides/building-search-ui/going-further/improve-performance/react/#turn-off-search-as-you-type
function queryHook(query: string, search: (value: string) => void) {
  if (timerId) {
    clearTimeout(timerId);
  }

  // Only trigger search if query meets minimum length or is empty (to reset)
  if (query.length >= MINIMUM_QUERY_LENGTH || query.length === 0) {
    timerId = setTimeout(() => {
      search(query);
    }, DEBOUNCE_IN_MS);
  }
}

interface Props {
  label: string;
}

export default function SearchForm({ label }: Props) {
  const t = useTranslations();
  const router = useRouter();
  // Determine initial value: URL param, then cookie, then undefined
  const initialValue =
    (router.query[TARGET_TAG_URL_PARAM] as string | undefined) ||
    (typeof window !== 'undefined'
      ? getCookie(TARGET_TAG_URL_PARAM)
      : undefined);
  const [selectedValue, setSelectedValue] = useState<string | undefined>(
    initialValue,
  );
  const { setIndexUiState } = useInstantSearch();
  const {
    store: { page },
  } = useStore();

  const handleTargetChange = (value: string) => {
    setSelectedValue(value);

    // Update the refinement list state
    setIndexUiState(state => ({
      ...state,
      refinementList: {
        ...state.refinementList,
        target_tags: value ? [value] : [], // Clear refinement if no value
      },
    }));

    // Set or clear cookie
    if (value) {
      setCookie(TARGET_TAG_URL_PARAM, value);
    } else {
      deleteCookie(TARGET_TAG_URL_PARAM);
    }
  };

  // On first mount, send query to data layer if it exists
  useEffect(() => {
    const query = Object.entries(router.query).find(([key]) =>
      key.includes('[query]'),
    );

    if (!query || !query[1]?.length) return;

    window.dataLayer?.push({
      event: 'search',
      query: query[1],
    });
  }, [router.query]);

  return (
    <>
      <div className={styles.container}>
        <div className={styles.wrapper}>
          <SearchBox
            classNames={{
              submit: styles.algoliaSubmit,
              input: styles.input,
              reset: styles.reset,
              form: styles.form,
            }}
            queryHook={queryHook}
            className={styles.searchBox}
            id={SEARCH_FORM_ID}
            placeholder={label}
            autoFocus
          />
          <Button
            form={SEARCH_FORM_ID}
            variant="brand"
            label={t.search}
            hideLabel
            icon="SEARCH"
            type="submit"
          />
        </div>
      </div>

      {!!page?.targetTags?.length && (
        <div className={styles.targetFilters}>
          <p className="u-mb-0">{t.targetFilterText}</p>
          <div className={styles.dropdownWrapper}>
            <DropDown
              id="target-filter"
              name={TARGET_TAG_URL_PARAM}
              items={page.targetTags.map(tag => ({
                label: tag.title,
                value: tag.title, // Algolia uses the label as the value
              }))}
              placeholder={t.targetFilterPlaceholder}
              value={selectedValue}
              onChange={handleTargetChange}
              className={styles.dropDown}
              updateUrl
            />
            <button
              type="button"
              className={styles.infoButton}
              aria-describedby={TARGET_FILTER_INFO_ID}
              data-tooltip-id={TARGET_FILTER_INFO_ID}
              aria-label={t.targetFilterInfo}
            >
              <Icon icon="INFO" />
            </button>
            <Tooltip
              id={TARGET_FILTER_INFO_ID}
              text={t.targetFilterInfoTooltip}
            />
          </div>
          {selectedValue && (
            <Configure
              optionalFilters={[
                `${TARGET_TAG_FILTER_NAME}:${selectedValue}<score=100>`,
              ]}
            />
          )}
        </div>
      )}
    </>
  );
}
